import type { TemplateLine } from "@/api/types";

/** A code no line has yet. A sum refers to a line by it, so it never changes once it's given. */
export function nextCode(lines: readonly TemplateLine[]): string {
  const used = new Set(lines.map((line) => line.code));
  let n = lines.length + 1;
  while (used.has(`X${n}`)) n++;
  return `X${n}`;
}

export function newLine(
  kind: "tag" | "sum" | "head",
  lines: readonly TemplateLine[],
): TemplateLine {
  return {
    code: nextCode(lines),
    kind,
    label: kind === "tag" ? "New line" : kind === "sum" ? "New subtotal" : "New heading",
    tags: [],
    formula: [],
    negate: false,
    strong: false,
    tie: null,
  };
}

export function move(lines: readonly TemplateLine[], index: number, by: -1 | 1): TemplateLine[] {
  const target = index + by;
  if (target < 0 || target >= lines.length) return [...lines];
  const next = [...lines];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/**
 * Takes a line out, and out of any sum that added it. A line that proves the statement has to stay, so it
 * can't be removed.
 */
export function remove(lines: readonly TemplateLine[], code: string): TemplateLine[] {
  if (lines.find((line) => line.code === code)?.tie) return [...lines];
  return lines
    .filter((line) => line.code !== code)
    .map((line) => ({
      ...line,
      formula: line.formula.filter((term) => term.slice(1) !== code),
    }));
}

/** The lines above this one a sum can add. A heading has no figure. */
export function addable(lines: readonly TemplateLine[], index: number): TemplateLine[] {
  return lines.slice(0, index).filter((line) => line.kind !== "head");
}

/** Tags already on another line, which would count twice. */
export function tagsElsewhere(lines: readonly TemplateLine[], code: string): Set<string> {
  return new Set(lines.filter((line) => line.code !== code).flatMap((line) => line.tags));
}

export const flip = (term: string) => (term.startsWith("-") ? "+" : "-") + term.slice(1);
